import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedProjectId, setActiveModelSubView } from '../../../store/slices/projectSlice'
import { setSelectedModelId } from '../../../store/slices/modelSlice'
import { Model } from '../../../types/models/model'
import { rateBBoxModel, rateClassificationModel } from '../../../utils'
// Consolidate stats via a single dataset-level call and derive per-source totals
// API mutations moved to sidebar component
import { Box, Tag, Star, Play } from 'lucide-react'
// ConfirmDialog moved to sidebar component
import ErrorDialog from '../../dialogs/ErrorDialog'
import {
  ListView,
  GridView as StyledGridView,
  EnhancedModelCard,
  CardHeader,
  StatusBadge,
  PerformanceSection,
  StarRating,
  MetricsGrid,
  MetricItem,
  EmptyState,
  ReadyToTrainMessage,
} from './styles'

// Local wrapper using Bootstrap utility classes instead of styled-components
const GalleryWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-100">{children}</div>
)

// GridView now imported from shared styles as StyledGridView

// All styled components are now imported from ./styles

interface ModelsGalleryProps {
  models?: Model[]
  viewMode?: 'grid' | 'list'
  project?: {
    annotation_type?: string
    id?: string
    datasetid?: string
  }
  selectedModelId?: string | null
  onModelClick?: (model: Model) => void
}

const ModelsGallery: React.FC<ModelsGalleryProps> = ({
  models: propModels = [],
  viewMode = 'grid',
  project,
  selectedModelId,
  onModelClick,
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  // Deployment functionality
  // API mutations and confirmation dialogs moved to sidebar
  const [error] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  // Get unique datasource combinations from all trained models
  const trainedModelsWithData = useMemo(() => {
    return propModels.filter(
      (model) =>
        model.state.toLowerCase() === 'trained' &&
        (model.parameters?.train_metadata?.datasource?.length || 0) > 0
    )
  }, [propModels])

  // We no longer need to build limited query combinations; we derive totals from a single stats call

  // Single dataset-level stats (no filter). We derive model-specific totals from datasource_distributions.
  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    undefined
  )

  // Create a map of model statistics
  const modelStatsMap = useMemo(() => {
    const statsMap = new Map<string, { images: string; annotations: string }>()
    const distributions = annoStats?.statistics?.datasource_distributions || {}

    trainedModelsWithData.forEach((model) => {
      const sources = model.parameters?.train_metadata?.datasource || []
      if (sources.length === 0) {
        statsMap.set(model.id, { images: '0', annotations: '0' })
        return
      }

      const totals = sources.reduce(
        (acc, src) => {
          const d = distributions[src]
          if (d) {
            acc.images += d.images || 0
            acc.annotations += d.annotations || 0
          }
          return acc
        },
        { images: 0, annotations: 0 }
      )

      statsMap.set(model.id, {
        images: totals.images.toLocaleString(),
        annotations: totals.annotations.toLocaleString(),
      })
    })

    return statsMap
  }, [trainedModelsWithData, annoStats?.statistics?.datasource_distributions])

  // Calculate training statistics for models
  const getTrainingStats = (model: Model) => {
    const trainingSources = model.parameters?.train_metadata?.datasource || []
    const isTrained = model.state.toLowerCase() === 'trained'

    // If no training sources, return 0
    if (trainingSources.length === 0) {
      return { images: '0', annotations: '0' }
    }

    // If this is a trained model and we have stats, use them
    if (isTrained && modelStatsMap.has(model.id)) {
      const value = modelStatsMap.get(model.id)
      return value ? value : { images: '—', annotations: '—' }
    }

    // For other trained models without fetched data
    if (isTrained) {
      return { images: '—', annotations: '—' }
    }

    // For non-trained models, show that they need training
    return { images: 'N/A', annotations: 'N/A' }
  }

  const handleModelClick = (model: Model) => {
    if (onModelClick) {
      onModelClick(model)
    } else {
      dispatch(
        setSelectedModelId({
          modelId: model.id,
          projectId: selectedProjectId || '',
        })
      )
      dispatch(setActiveModelSubView('Performance'))
      navigate(`/models/${selectedProjectId}/performance`)
    }
  }

  // Action handlers moved to sidebar component

  // Find best and worst models
  const getModelRatings = (models: Model[]) => {
    if (!models || !project) return { bestModelId: null, worstModelId: null }

    const modelRatings = models.map((model) => {
      const rating =
        project.annotation_type === 'classification'
          ? rateClassificationModel(model).classificationRating
          : rateBBoxModel(model).bBoxRating
      return { modelId: model.id, rating }
    })

    modelRatings.sort((a, b) => b.rating - a.rating)
    return {
      bestModelId: modelRatings[0]?.modelId || null,
      worstModelId: modelRatings[modelRatings.length - 1]?.modelId || null,
    }
  }

  const { bestModelId, worstModelId } = getModelRatings(propModels)

  const getModelMetrics = (model: Model) => {
    const precision = Number(model.metrics?.['validation:precision']) || 0
    const recall = Number(model.metrics?.['validation:recall']) || 0
    const mAP = Number(model.metrics?.['validation:mAP50-95']) || 0
    const accuracy = Number(model.metrics?.['validation:accuracy']) || 0
    const trainAccuracy = Number(model.metrics?.['train:accuracy']) || 0
    const valAccuracy = Number(model.metrics?.['validation:accuracy']) || 0

    // Calculate F1 score
    const f1 = precision && recall ? (2 * (precision * recall)) / (precision + recall) : 0

    // For classification models, only show accuracy; for object detection, show all metrics
    const isClassification = project?.annotation_type === 'classification'

    return {
      precision: isClassification ? '-' : (precision * 100).toFixed(1) + '%',
      recall: isClassification ? '-' : (recall * 100).toFixed(1) + '%',
      f1: isClassification ? '-' : (f1 * 100).toFixed(1) + '%',
      mAP: isClassification ? '-' : (mAP * 100).toFixed(1) + '%',
      accuracy: (accuracy * 100).toFixed(1) + '%',
      trainAccuracy: (trainAccuracy * 100).toFixed(1) + '%',
      valAccuracy: (valAccuracy * 100).toFixed(1) + '%',
      isClassification,
    }
  }

  const getPerformanceRating = (model: Model) => {
    if (!project) return 0
    return project.annotation_type === 'classification'
      ? rateClassificationModel(model).classificationRating
      : rateBBoxModel(model).bBoxRating
  }

  const renderStars = (rating: number) => {
    const stars = []
    const size = 14

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={size}
          fill={i <= rating ? '#ffd700' : 'none'}
          color={i <= rating ? '#ffd700' : 'rgba(255, 255, 255, 0.3)'}
        />
      )
    }
    return stars
  }

  const getDeploymentStatus = (model: Model) => {
    const deploymentStatus = model.deployment?.status?.toLowerCase()
    const isDeployed = deploymentStatus === 'in_service'
    const isDeploying = deploymentStatus === 'creating'
    return { isDeployed, isDeploying, deploymentStatus }
  }

  if (propModels.length === 0) {
    return (
      <EmptyState>
        <h3>No models found</h3>
        <p>Try adjusting your search or filter criteria.</p>
      </EmptyState>
    )
  }

  const renderModel = (model: Model, index: number) => {
    const variant =
      model.id === bestModelId && propModels.length > 1
        ? 'best'
        : model.id === worstModelId && propModels.length > 1
        ? 'worst'
        : undefined

    const metrics = getModelMetrics(model)
    const rating = getPerformanceRating(model)
    const { isDeployed, isDeploying } = getDeploymentStatus(model)
    const isTrained = model.state.toLowerCase() === 'trained'
    const isInitialized =
      model.state.toLowerCase() === 'initialised' || model.state.toLowerCase() === 'initialized'
    const isConfigured =
      (model.parameters?.train_metadata?.datasource?.length || 0) > 0 &&
      (model.parameters?.validation_metadata?.datasource?.length || 0) > 0

    // Get training statistics for this model
    const trainingStats = getTrainingStats(model)

    // Calculate training duration for trained models
    const getTrainingDuration = () => {
      if (!model.train_started_at || !model.train_ended_at) return 'N/A'

      const trainStart = new Date(model.train_started_at)
      const trainEnd = new Date(model.train_ended_at)

      // Check if training is still in progress
      if (model.train_ended_at === '0001-01-01T00:00:00Z') {
        return 'In Progress'
      }

      const durationMs = trainEnd.getTime() - trainStart.getTime()
      const hours = Math.floor(durationMs / (1000 * 60 * 60))
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      } else {
        return `${minutes}m`
      }
    }

    // Common model header content
    const modelHeader = (
      <CardHeader $viewMode={viewMode}>
        <div className="model-name-row">
          <h3 className="model-name">{model.name}</h3>
        </div>
        <div className="model-details-row">
          <div
            className={`model-type ${
              project?.annotation_type === 'classification' ? 'classification' : 'object-detection'
            }`}
          >
            {project?.annotation_type === 'classification' ? (
              <>
                <Tag size={12} />
                Classification
              </>
            ) : (
              <>
                <Box size={12} />
                Object Detection
              </>
            )}
          </div>
          {viewMode === 'grid' && (
            <StatusBadge $status={isDeployed ? 'deployed' : isDeploying ? 'creating' : model.state}>
              {isDeployed ? 'Deployed' : isDeploying ? 'Deploying' : model.state}
            </StatusBadge>
          )}
        </div>
      </CardHeader>
    )

    // Performance content for trained models
    const performanceContent = isTrained && (
      <PerformanceSection $viewMode={viewMode}>
        <StarRating>
          <div className="stars-container">{renderStars(rating)}</div>
        </StarRating>

        {project?.annotation_type === 'classification' ? (
          <MetricsGrid>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">Training Acc</div>
              <div className="metric-value">{metrics.trainAccuracy}</div>
            </MetricItem>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">Validation Acc</div>
              <div className="metric-value">{metrics.valAccuracy}</div>
            </MetricItem>
          </MetricsGrid>
        ) : (
          <MetricsGrid>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">Precision</div>
              <div className="metric-value">{metrics.precision}</div>
            </MetricItem>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">Recall</div>
              <div className="metric-value">{metrics.recall}</div>
            </MetricItem>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">F1 Score</div>
              <div className="metric-value">{metrics.f1}</div>
            </MetricItem>
            <MetricItem $viewMode={viewMode}>
              <div className="metric-label">mAP</div>
              <div className="metric-value">{metrics.mAP}</div>
            </MetricItem>
          </MetricsGrid>
        )}
      </PerformanceSection>
    )

    // Ready to train message for initialized models
    const readyToTrainContent = isInitialized && (
      <ReadyToTrainMessage>
        <div className="icon">
          <Play size={32} />
        </div>
        <h4 className="title">{isConfigured ? 'Ready to Train' : 'Configure Training'}</h4>
        <p className="subtitle">
          {isConfigured
            ? 'This model is configured and ready to begin training'
            : 'Configure training and validation datasets to begin training'}
        </p>
      </ReadyToTrainMessage>
    )

    return (
      <EnhancedModelCard
        key={model.id}
        $variant={variant}
        $viewMode={viewMode}
        $isSelected={selectedModelId === model.id}
        onClick={() => handleModelClick(model)}
      >
        {viewMode === 'list' ? (
          // List view: table-like horizontal layout matching SelectModelTreeModal
          <>
            <div
              className={`model-icon ${
                project?.annotation_type === 'classification'
                  ? 'classification'
                  : 'object-detection'
              }`}
            >
              {project?.annotation_type === 'classification' ? (
                <Tag size={20} />
              ) : (
                <Box size={20} />
              )}
            </div>

            <div className="model-info">
              <div className="model-name-section">
                <h5 className="model-name">{model.name}</h5>
              </div>

              {isTrained && (
                <div className="model-metrics">
                  {/* Training Data Group */}
                  <div className="metric-item">
                    <div className="metric-value">{trainingStats.images}</div>
                    <div className="metric-label">Images</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">{trainingStats.annotations}</div>
                    <div className="metric-label">Annotations</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.train_metadata?.datasource?.length || 0}
                    </div>
                    <div className="metric-label">Train Sets</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.validation_metadata?.datasource?.length || 0}
                    </div>
                    <div className="metric-label">Val Sets</div>
                  </div>

                  {/* Performance Group */}
                  {project?.annotation_type === 'classification' ? (
                    <>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.trainAccuracy}</div>
                        <div className="metric-label">Training Acc</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.valAccuracy}</div>
                        <div className="metric-label">Validation Acc</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.precision}</div>
                        <div className="metric-label">Precision</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.recall}</div>
                        <div className="metric-label">Recall</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.f1}</div>
                        <div className="metric-label">F1 Score</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{metrics.mAP}</div>
                        <div className="metric-label">mAP</div>
                      </div>
                    </>
                  )}

                  {/* Training Info Group */}
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.hyperparameters?.hyperparameters?.training?.epochs
                        ?.value || '-'}
                    </div>
                    <div className="metric-label">Epochs</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">{getTrainingDuration()}</div>
                    <div className="metric-label">Duration</div>
                  </div>
                </div>
              )}

              {!isTrained && (
                <div className="model-metrics">
                  {/* Training Data Group - Same as trained models */}
                  <div className="metric-item">
                    <div className="metric-value">{trainingStats.images}</div>
                    <div className="metric-label">Images</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">{trainingStats.annotations}</div>
                    <div className="metric-label">Annotations</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.train_metadata?.datasource?.length || 0}
                    </div>
                    <div className="metric-label">Train Sets</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.validation_metadata?.datasource?.length || 0}
                    </div>
                    <div className="metric-label">Val Sets</div>
                  </div>

                  {/* Performance Group - All unavailable for untrained models */}
                  {project?.annotation_type === 'classification' ? (
                    <>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">Training Acc</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">Validation Acc</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">Precision</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">Recall</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">F1 Score</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">-</div>
                        <div className="metric-label">mAP</div>
                      </div>
                    </>
                  )}

                  {/* Training Info Group - Same as trained models */}
                  <div className="metric-item">
                    <div className="metric-value">
                      {model.parameters?.hyperparameters?.hyperparameters?.training?.epochs
                        ?.value || '-'}
                    </div>
                    <div className="metric-label">Epochs</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-value">-</div>
                    <div className="metric-label">Duration</div>
                  </div>
                </div>
              )}
            </div>

            <div className="model-badges">
              <StatusBadge
                $status={isDeployed ? 'deployed' : isDeploying ? 'creating' : model.state}
              >
                {isDeployed ? 'Deployed' : isDeploying ? 'Deploying' : model.state}
              </StatusBadge>
            </div>
          </>
        ) : (
          // Grid view: vertical layout with full details
          <>
            {modelHeader}
            {performanceContent}
            {readyToTrainContent}
          </>
        )}
      </EnhancedModelCard>
    )
  }

  return (
    <GalleryWrapper>
      {viewMode === 'grid' ? (
        <StyledGridView>
          {propModels.map((model, index) => renderModel(model, index))}
        </StyledGridView>
      ) : (
        <ListView>{propModels.map((model, index) => renderModel(model, index))}</ListView>
      )}

      {/* Confirmation dialogs moved to sidebar component */}

      {/* Error Dialog */}
      <ErrorDialog show={showError} onClose={() => setShowError(false)} message={error || ''} />
    </GalleryWrapper>
  )
}

export default ModelsGallery
